const EVENT_GALLERY_IMAGE_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "webp",
  "gif",
  "heic",
  "heif",
]);

const EVENT_GALLERY_VIDEO_EXTENSIONS = new Set([
  "mp4",
  "mov",
  "m4v",
  "webm",
  "avi",
  "mkv",
]);

export const EVENT_GALLERY_UPLOAD_CONCURRENCY = 3;

function getFileExtension(fileName) {
  const match = String(fileName || "").toLowerCase().match(/\.([a-z0-9]+)$/);
  return match ? match[1] : "";
}

export function getEventGalleryFileType(file) {
  const contentType = String(file?.type || "").trim().toLowerCase();
  const extension = getFileExtension(file?.name);

  if (contentType.startsWith("image/") || EVENT_GALLERY_IMAGE_EXTENSIONS.has(extension)) {
    return "image";
  }
  if (contentType.startsWith("video/") || EVENT_GALLERY_VIDEO_EXTENSIONS.has(extension)) {
    return "video";
  }
  return "";
}

function getPreparedUpload(response) {
  const uploads = Array.isArray(response?.data?.uploads) ? response.data.uploads : [];
  const upload = uploads[0];
  if (uploads.length !== 1 || !upload?.id || !upload?.upload_url) {
    throw new Error("Could not prepare this media upload.");
  }
  return upload;
}

function shouldRetryCompletion(error) {
  const status = Number(error?.response?.status || 0);
  return !status || status >= 500;
}

function shouldRetryCloudUpload(error) {
  const status = Number(error?.status || 0);
  return !status || status === 408 || status === 429 || status >= 500;
}

async function uploadPreparedFile(uploadFile, preparedUpload, draft, onProgress) {
  try {
    await uploadFile(preparedUpload, draft, onProgress);
  } catch (error) {
    if (!shouldRetryCloudUpload(error)) throw error;
    onProgress(0, Number(draft.file?.size || 0));
    await uploadFile(preparedUpload, draft, onProgress);
  }
}

async function uploadPreparedThumbnail(uploadFile, preparedUpload, draft) {
  if (!draft?.posterFile || !preparedUpload?.thumbnail_upload_url) return;
  const thumbnailUpload = {
    upload_url: preparedUpload.thumbnail_upload_url,
    method: "PUT",
    headers: preparedUpload.thumbnail_headers || { "Content-Type": "image/jpeg" },
  };
  const thumbnailDraft = {
    ...draft,
    name: `${draft.name || "video"}-thumbnail.jpg`,
    file: draft.posterFile,
  };
  try {
    await uploadPreparedFile(uploadFile, thumbnailUpload, thumbnailDraft, () => {});
  } catch {
    // A missing thumbnail must not discard an otherwise valid video upload.
    // Completion verification clears the thumbnail metadata when the PUT failed.
  }
}

async function completeUploads(apiClient, completeUrl, successfulEntries) {
  const payload = {
    uploads: successfulEntries.map(({ upload }) => ({ id: upload.id })),
  };

  try {
    return await apiClient.post(completeUrl, payload);
  } catch (error) {
    if (!shouldRetryCompletion(error)) throw error;
    return apiClient.post(completeUrl, payload);
  }
}

export async function uploadGalleryDrafts({
  apiClient,
  prepareUrl,
  completeUrl,
  deleteUrlForId,
  preparePayload = {},
  drafts,
  uploadFile,
  concurrency = EVENT_GALLERY_UPLOAD_CONCURRENCY,
  cleanupFailedPrepared = true,
  onFileProgress = () => {},
  onSaving = () => {},
}) {
  const queue = Array.isArray(drafts) ? drafts : [];
  if (!queue.length) {
    return {
      uploadedItems: [],
      successfulDrafts: [],
      failedDrafts: [],
      failures: [],
    };
  }

  const successfulEntries = [];
  const failedEntries = [];
  let nextIndex = 0;

  const uploadNext = async () => {
    while (nextIndex < queue.length) {
      const index = nextIndex;
      nextIndex += 1;
      let draft = queue[index];
      let preparedUpload = null;

      try {
        if (!draft.posterFile && draft.posterPromise) {
          const poster = await draft.posterPromise.catch(() => null);
          if (poster?.posterFile) draft = { ...draft, ...poster };
        }
        const fileMetadata = {
          name: draft.name || draft.file?.name || "event-media",
          content_type: draft.file?.type || "",
          size_bytes: draft.file?.size || 0,
        };
        if (draft.clientUploadId) {
          fileMetadata.client_upload_id = draft.clientUploadId;
        }
        if (draft.posterFile) {
          fileMetadata.thumbnail_content_type = draft.posterFile.type || "image/jpeg";
          fileMetadata.thumbnail_size_bytes = draft.posterFile.size || 0;
        }
        const prepareResponse = await apiClient.post(prepareUrl, {
          ...preparePayload,
          files: [fileMetadata],
        });
        preparedUpload = getPreparedUpload(prepareResponse);
        await uploadPreparedFile(
          uploadFile,
          preparedUpload,
          draft,
          (loaded, total) => {
            onFileProgress(index, loaded, total);
          }
        );
        await uploadPreparedThumbnail(uploadFile, preparedUpload, draft);
        successfulEntries.push({ draft, index, upload: preparedUpload });
      } catch (error) {
        failedEntries.push({ draft, index, error });
        onFileProgress(index, 0, Number(draft.file?.size || 0));

        if (cleanupFailedPrepared && preparedUpload?.id) {
          try {
            await apiClient.delete(deleteUrlForId(preparedUpload.id));
          } catch {
            // The stale pending upload can be cleaned up server-side later.
          }
        }
      }
    }
  };

  const workerCount = Math.max(1, Math.min(Number(concurrency) || 1, queue.length));
  await Promise.all(Array.from({ length: workerCount }, () => uploadNext()));

  successfulEntries.sort((left, right) => left.index - right.index);
  failedEntries.sort((left, right) => left.index - right.index);

  let uploadedItems = [];
  if (successfulEntries.length) {
    onSaving(successfulEntries);
    const completeResponse = await completeUploads(apiClient, completeUrl, successfulEntries);
    uploadedItems = Array.isArray(completeResponse?.data?.items) ? completeResponse.data.items : [];
  }

  return {
    uploadedItems,
    successfulDrafts: successfulEntries.map(({ draft }) => draft),
    failedDrafts: failedEntries.map(({ draft }) => draft),
    failures: failedEntries.map(({ draft, error }) => ({ draft, error })),
  };
}

export function uploadEventGalleryDrafts({ apiClient, eventId, ...options }) {
  const clubPublicationConsent = Boolean(options.clubPublicationConsent);
  const uploadOptions = { ...options };
  delete uploadOptions.clubPublicationConsent;
  return uploadGalleryDrafts({
    apiClient,
    prepareUrl: `/events/${eventId}/gallery/upload-url/`,
    completeUrl: `/events/${eventId}/gallery/complete/`,
    deleteUrlForId: (mediaId) => `/events/${eventId}/gallery/${mediaId}/`,
    preparePayload: { club_publication_consent: clubPublicationConsent },
    ...uploadOptions,
  });
}

export function uploadClubGalleryDrafts({ apiClient, clubId, ...options }) {
  return uploadGalleryDrafts({
    apiClient,
    prepareUrl: `/clubs/${clubId}/gallery/upload-url/`,
    completeUrl: `/clubs/${clubId}/gallery/complete/`,
    deleteUrlForId: (mediaId) => `/clubs/${clubId}/gallery/${mediaId}/`,
    ...options,
  });
}

export function uploadEventPromoDrafts({ apiClient, eventId, ...options }) {
  return uploadGalleryDrafts({
    apiClient,
    prepareUrl: `/events/${eventId}/promo-gallery/upload-url/`,
    completeUrl: `/events/${eventId}/promo-gallery/complete/`,
    deleteUrlForId: (mediaId) => `/events/${eventId}/promo-gallery/${mediaId}/`,
    ...options,
  });
}

export async function uploadPreparedMediaFile(upload, draft, onProgress = () => {}) {
  const file = draft?.file;
  const response = await fetch(upload.upload_url, {
    method: upload.method || "PUT",
    headers: upload.headers || { "Content-Type": file?.type || "application/octet-stream" },
    body: file,
  });
  if (!response.ok) {
    const error = new Error("Cloud upload failed.");
    error.status = response.status;
    throw error;
  }
  onProgress(Number(file?.size || 0), Number(file?.size || 0));
}

export function uploadPreparedMediaFileWithProgress(upload, draft, onProgress = () => {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(upload.method || "PUT", upload.upload_url, true);
    const headers = upload.headers && Object.keys(upload.headers).length
      ? upload.headers
      : { "Content-Type": draft.file?.type || "application/octet-stream" };
    Object.entries(headers).forEach(([key, value]) => {
      if (value !== undefined && value !== null) xhr.setRequestHeader(key, String(value));
    });
    xhr.upload.onprogress = (event) => {
      onProgress(Number(event.loaded || 0), Number(event.total || draft.file?.size || 0));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const size = Number(draft.file?.size || 0);
        onProgress(size, size);
        resolve(xhr);
        return;
      }
      const error = new Error(`Cloud upload failed for ${draft.name || "media file"}.`);
      error.status = xhr.status;
      reject(error);
    };
    xhr.onerror = () => {
      const error = new Error(`Cloud upload failed for ${draft.name || "media file"}.`);
      error.status = xhr.status;
      reject(error);
    };
    xhr.onabort = () => reject(new Error(`Cloud upload was cancelled for ${draft.name || "media file"}.`));
    xhr.send(draft.file);
  });
}
